import React from "react";
import { toast } from "react-toastify";

import { Stack } from "@shopify/polaris";

import DocumentUploader from "./DocumentUploader";
import FileUploader, { FileInvalidReason } from "./FileUploader";
import GridProperties from "./GridProperties";

import { SystemMessage } from "../../config/SystemMessage";

import styles from "./ArtifactGrid.module.css";
import { useDispatch, useSelector } from "react-redux";
import { AssetState } from "../../types/AssetState";
import { uploadAssets } from "../../store/actions/Asset";
import Asset from "../../types/Asset";
import { AssetType } from "../../types/AssetType";
import InspectionElement from "../../types/InspectionElement";
import { containsIllegalCharacters } from "../../validators/validators";
import { useTranslation } from "react-i18next";
import * as util from "util";
import ReactGA from "react-ga4";

const MAX_DOCUMENTS_TO_SHOW = 3;
const FILE_REJECTED_MESSAGE_TIMEOUT = 12000;

export default function DocumentGrid({
    elementId,
    assetSize,
    deactivated
}: GridProperties) {
    const documentTypes =
        "image/jpeg, image/png, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/zip, application/x-zip-compressed";
    const dispatch = useDispatch();
    const activeInspection = useSelector(
        (state: any) => state.portal.activeInspection
    );

    const { t } = useTranslation("common");
    const user = useSelector((state: any) => state.portal.user);

    let currentElement: InspectionElement;
    let maxDocumentsToDisplay = 0;
    let documents: any[] = [];

    if (activeInspection) {
        currentElement = activeInspection.sections.find(
            (section: any) => section.Id === elementId
        );
        documents = currentElement.Documents;
        maxDocumentsToDisplay = currentElement.MaxItems;
    }

    const handleDropZoneDrop = (_dropFiles : any, acceptedFiles : any, _rejectedFiles : any) => {
            if (_rejectedFiles.length > 0) {
                if (_rejectedFiles[0].rejectReason === FileInvalidReason.SIZE) {
                    toast.error(t(SystemMessage.DOCUMENT_OVER_SIZE_LIMIT));
                    ReactGA.event({
                        category: "Document Download",
                        action: "failed",
                        label: "too large",
                        value: 0
                    });
                    return;
                }

                if (
                    _rejectedFiles[0].rejectReason ===
                    FileInvalidReason.ZERO_BYTE
                ) {
                    toast.error(t(SystemMessage.FILES_ZERO_BYTE));
                    ReactGA.event({
                        category: "Document Download",
                        action: "failed",
                        label: "zero byte",
                        value: 0
                    });
                    return;
                }

                toast.error(t(SystemMessage.INVALID_FILES_FOR_DOCUMENT_UPLOAD));
                ReactGA.event({
                    category: "Document Download",
                    action: "failed",
                    label: "invalid types",
                    value: 0
                });
                return;
            }

            if (
                acceptedFiles.length + currentElement.Documents.length >
                maxDocumentsToDisplay
            ) {
                const maxDocumentsMessage = util.format(
                    t(SystemMessage.MAX_DOCUMENTS_UPLOADED),
                    String(maxDocumentsToDisplay)
                );
                toast.error(maxDocumentsMessage);
                ReactGA.event({
                    category: "Document Download",
                    action: "failed",
                    label: "max limit reached",
                    value: 0
                });
                return;
            }

            const assets: Asset[] = [];
            for (let i = 0; i !== acceptedFiles.length; i++) {
                if (containsIllegalCharacters(acceptedFiles[i].name)) {
                    toast.error(t(SystemMessage.INVALID_FILE_NAME), {
                        autoClose: FILE_REJECTED_MESSAGE_TIMEOUT
                    });
                    ReactGA.event({
                        category: "Document Download",
                        action: "failed",
                        label: "invalid filename",
                        value: 0
                    });
                    continue;
                }

                assets.push({
                    name: acceptedFiles[i].name,
                    url: URL.createObjectURL(acceptedFiles[i]),
                    file: acceptedFiles[i],
                    state: AssetState.UPLOADING,
                    progress: 0,
                    type: AssetType.Document,
                    metadata: {},
                    reusable: currentElement.ReUseDocuments,
                    ...(currentElement.ReUseDocuments && currentElement.expiryDate && {
                        expiryTime: currentElement.expiryDate
                    })
                });
            }

            if (assets.length > 0) {
                dispatch(uploadAssets(activeInspection?.Id, elementId, assets, undefined, user));
            }
        }

    function EmptyDocumentGrid() {
        const fileUploader = (
            <FileUploader
                onDrop={handleDropZoneDrop}
                accept={documentTypes}
                type="document"
                deactivated={deactivated}
            />
        );
        return (
            <div className={styles.emptyDocumentDropzone}>{fileUploader}</div>
        );
    }

    function singleDocumentUploader() {
        return (
            <DocumentUploader
                id={0}
                key={0}
                type={"medium"}
                elementId={elementId}
            />
        );
    }

    function documentStack() {
        return (
            <Stack
                distribution="equalSpacing"
                alignment="center"
                spacing="extraTight"
            >
                {documents.map((doc, idx) => (
                    <Stack key={doc.name}>
                        <DocumentUploader
                            id={idx}
                            key={idx}
                            type={assetSize}
                            elementId={elementId}
                        />
                    </Stack>
                ))}
                {documents.length < maxDocumentsToDisplay ? (
                    <div className={styles.fileUploaderSmall}>
                        <FileUploader
                            onDrop={handleDropZoneDrop}
                            accept={documentTypes}
                            type="document"
                            deactivated={deactivated}
                        />
                    </div>
                ) : (
                    <></>
                )}
            </Stack>
        );
    }

    function documentStackLarge() {
        return (
            <Stack>
                {documents.map((doc, idx) => (
                    <Stack key={doc.name}>
                        <DocumentUploader
                            id={idx}
                            key={idx}
                            type={"large"}
                            remaining={"?"}
                            elementId={elementId}
                        />
                    </Stack>
                ))}
                {documents.length < maxDocumentsToDisplay ? (
                    <Stack key={"FileUploader"}>
                        <div className={styles.fileUploaderLarge}>
                            <FileUploader
                                onDrop={handleDropZoneDrop}
                                accept={documentTypes}
                                type="document"
                                deactivated={deactivated}
                            />
                        </div>
                    </Stack>
                ) : (
                    <div />
                )}
            </Stack>
        );
    }

    function documentStackWithRemainingHighlight() {
        const documentsVisibleInChildView =
            "+" + (documents.length - MAX_DOCUMENTS_TO_SHOW);
        const documentsVisible = documents.slice(0, MAX_DOCUMENTS_TO_SHOW);

        return (
            <Stack
                distribution="equalSpacing"
                alignment="center"
                spacing="extraTight"
            >
                {documentsVisible.map((doc, idx) => {
                    let type = "small";
                    let remainingText = "";

                    if (idx === documentsVisible.length - 1) {
                        type = "remaining";
                        remainingText = documentsVisibleInChildView;
                    }
                    return (
                        <Stack key={doc.name}>
                            <DocumentUploader
                                id={idx}
                                key={idx}
                                remaining={remainingText}
                                type={type}
                                elementId={elementId}
                            />
                        </Stack>
                    );
                })}
                {documents.length < maxDocumentsToDisplay ? (
                    <div className={styles.fileUploaderSmall}>
                        <FileUploader
                            onDrop={handleDropZoneDrop}
                            accept={documentTypes}
                            type="document"
                            deactivated={deactivated}
                        />
                    </div>
                ) : (
                    <></>
                )}
            </Stack>
        );
    }

    function DocumentGrid() {
        if (assetSize === "large") {
            return documentStackLarge();
        }

        if (documents.length === 0) {
            return <EmptyDocumentGrid />;
        }

        if (
            documents.length === 1 &&
            documents.length === maxDocumentsToDisplay
        ) {
            return singleDocumentUploader();
        }

        if (documents.length > MAX_DOCUMENTS_TO_SHOW) {
            return documentStackWithRemainingHighlight();
        }

        return documentStack();
    }

    return <div className={styles.documentGrid}>{DocumentGrid()}</div>;
}
